import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../ui/typography/Text';
import { AccentButton } from '../../ui/buttons/AccentButton';
import { CoinFlips } from './CoinFlips';
import { ResultsGraph } from './ResultsGraph';
import { Brain, BookOpen, Microscope, BarChart3 } from 'lucide-react';

const TemplatedGamblersFallacySimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [currentFlip, setCurrentFlip] = useState<boolean | null>(null);
  const [flips, setFlips] = useState<boolean[]>([]);
  const [results, setResults] = useState<Array<{
    streak: number;
    occurrences: number;
    successes: number;
    probability: number;
  }>>([]);
  const [showResults, setShowResults] = useState(false);
  const maxFlips = 3000;
  const flipSpeed = 5;

  // Reference for scrolling
  const simulationRef = useRef<HTMLDivElement>(null);

  // ========== EVENT HANDLERS ==========
  const runSimulation = () => {
    setIsSimulating(true);
    setShowResults(false);
    setFlips([]);
    setResults([]);
    setCurrentFlip(null);
    
    let flipCount = 0;
    const newFlips: boolean[] = [];

    const simulateFlip = () => {
      const result = Math.random() < 0.5;
      newFlips.push(result);
      setCurrentFlip(result);
      flipCount++;
      
      if (flipCount % 5 === 0 || flipCount === maxFlips) {
        setFlips([...newFlips]);
      }

      // Reset streak counts for this analysis
      const currentStreakCounts = Array(5).fill(null).map((_, i) => ({
        streak: i,
        occurrences: 0,
        successes: 0,
        probability: 0.5
      }));

      // Analyze all sequences in our flips
      for (let i = 0; i < newFlips.length - 1; i++) {
        // Count current streak at this position
        let currentStreak = 0;
        for (let j = i; j >= 0; j--) {
          if (newFlips[j]) {
            currentStreak++;
          } else {
            break;
          }
        }
        
        if (currentStreak <= 4) {
          currentStreakCounts[currentStreak].occurrences++;
          if (newFlips[i + 1]) {  // If next flip is heads
            currentStreakCounts[currentStreak].successes++;
          }
        }
      }

      // Calculate probabilities
      const updatedResults = currentStreakCounts.map(count => ({
        ...count,
        probability: count.occurrences > 0 ? count.successes / count.occurrences : 0.5
      }));

      if (flipCount % 20 === 0 || flipCount === maxFlips) {
        setResults(updatedResults);
      }

      if (flipCount >= maxFlips) {
        setIsSimulating(false);
        setShowResults(true);
        return;
      }

      setTimeout(simulateFlip, flipSpeed);
    };

    simulateFlip();
  };

  // Reset the simulation
  const handleReset = () => {
    setStarted(false);
    setIsSimulating(false);
    setShowResults(false);
    setFlips([]);
    setResults([]);
    setCurrentFlip(null);
    
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ========== SIMULATION-SPECIFIC DATA ==========
  const simulationData = {
    title: "The Gambler's Fallacy",
    description: "Discover why people believe that after several heads, a coin is 'due' for tails - even though each flip remains an independent 50/50 chance.",
    citation: "Freedman & Sears (1965), \"Selective Exposure,\" Advances in Experimental Social Psychology"
  };

  // ========== COMPONENT CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      <SimulationStep 
        title="Watch the Coin Flips" 
        isActive={!showResults}
      >
        <div className="space-y-6">
          <Text className="text-gray-700">
            This simulation runs 3,000 coin flips to demonstrate the Gambler's Fallacy. 
            Click the button to observe how the probability of getting heads remains constant at 50%, 
            regardless of previous outcomes.
          </Text>
          <div className="flex flex-col items-center gap-6">
            <div className="w-full max-w-md">
              {(isSimulating || showResults) && (
                <CoinFlips 
                  currentFlip={currentFlip}
                  flips={flips}
                  maxFlips={maxFlips}
                />
              )}
            </div>
            {!isSimulating && !showResults && (
              <AccentButton 
                onClick={runSimulation}
                disabled={isSimulating || showResults}
              >
                Run 3,000 Flips
              </AccentButton>
            )}
          </div>
        </div>
      </SimulationStep>

      {/* RESULTS SECTION */}
      {showResults && (
        <div className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-5">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                <Text className="text-gray-700">
                  After simulating 3,000 coin flips, we can see how the probability of getting heads 
                  remains constant at 50%, regardless of previous outcomes. This demonstrates that 
                  each coin flip is an independent event.
                </Text>
                
                <ResultMessage 
                  isCorrect={true} 
                  message="Notice how the actual probability (blue line) stays close to 50%, while human intuition (orange line) incorrectly expects the probability to decrease after consecutive identical outcomes."
                />
                
                <GraphContainer>
                  <div className="overflow-x-auto overflow-y-hidden">
                    <div className="min-w-[400px]">
                      <ResultsGraph results={results} />
                    </div>
                  </div>
                  {/* Mobile-friendly legend */}
                  <div className="mt-4 flex flex-col sm:hidden items-center gap-3">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-indigo-500" />
                        <span className="text-sm text-gray-700">Simulated Probability</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-orange-500" />
                        <span className="text-sm text-gray-700">Human Perception</span>
                      </div>
                    </div>
                  </div>
                </GraphContainer>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-indigo-50 rounded-xl">
                    <Text className="font-bold text-indigo-700 mb-2">
                      Simulated Results (Blue Line)
                    </Text>
                    <Text className="text-gray-700">
                      Each coin flip is an independent event with exactly 50% probability. With 3,000 flips, 
                      the law of large numbers ensures that our simulated probability converges precisely to 50%. 
                      The more flips we perform, the closer our observed probability gets to this true value, 
                      regardless of any previous streak of heads or tails.
                    </Text>
                  </div>

                  <div className="p-4 bg-orange-100 rounded-xl">
                    <Text className="font-bold text-orange-700 mb-2">
                      Human Intuition (Orange Line)
                    </Text>
                    <Text className="text-gray-700">
                      The orange line illustrates findings from psychological studies, where 
                      participants consistently underestimated the likelihood of continuation after a streak. 
                      Our brains evolved to find patterns, leading us to believe that after several heads, 
                      tails becomes "due."
                    </Text>
                  </div>
                </div>
              </div>
              
              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to the Gambler's Fallacy
                </Text>
                <Text className="text-gray-700">
                  The Gambler's Fallacy is the mistaken belief that if something happens more frequently than normal during a given period, 
                  it will happen less frequently in the future (or vice versa). In reality, random events like coin flips are independent 
                  of each other - the coin has no "memory" of previous flips, so the probability remains constant at 50% regardless of what 
                  happened before.
                </Text>
                <Text className="text-gray-700">
                  This cognitive bias leads people to make irrational decisions in gambling and other scenarios involving probability. 
                  For example, a person might believe they're "due for a win" after several losses, or that a certain lottery number is 
                  less likely to appear because it came up recently.
                </Text>
              </div>
              
              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  In 1965, Freedman and Sears conducted a study where participants were asked to 
                  predict coin flip outcomes. They found that people consistently underestimated 
                  the likelihood of a streak continuing, showing a strong belief that a change 
                  was "due" after several identical outcomes.
                </Text>
                <Text className="text-gray-700">
                  The study revealed that after seeing:
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>1 head: People estimated 40% chance of another head</li>
                  <li>2 heads: Estimates dropped to 35%</li>
                  <li>3 heads: Further dropped to 25%</li>
                  <li>4 heads: Only 20% expected another head</li>
                </ul>
                <Text className="text-gray-700 text-sm italic">
                  Note: These percentage values are approximate representations for educational purposes. The exact numerical data follows the pattern of declining probability estimates that participants demonstrated as streak length increased.
                </Text>
                <Text className="text-gray-700">
                  In reality, the probability remained constant at 50% throughout.
                </Text>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by {simulationData.citation}. The study examined how people's predictions of random events are influenced by their observation of previous outcomes.
              </Text>
              
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title={simulationData.title}
      description={simulationData.description}
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default TemplatedGamblersFallacySimulation; 